import React, { ReactElement, useState } from 'react';
import classnames from 'classnames';
import { Activity, EActivityType, User } from 'v2/models';
import styles from './activityItem.module.scss';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import ActivityItemDescription from './ActivityItemDescription';
import ActivityItemRequest from './ActivityItemRequest';
import HtmlRenderer from 'v2/common/HtmlRenderer/HtmlRenderer';
import { formatTextNewlines } from 'v2/utils/textFormat';
import {
  useActivityInfo,
  getActivityTitle,
  getActivityContactsEmail,
} from 'v2/utils/activityUtils';
import { ReactComponent as ActivitySVG } from 'v2/icons/activityNote.svg';
import { formatDate } from 'v2/utils/dateUtils';
import { ActivityItemLinkCreated } from './components';

export const MAX_ACTIVITY_DESCRIPTION_LINES = 3;

interface ActivityItemProps {
  activity: Activity;
  /**
   * @default true
   */
  showsActions?: boolean;
  actionsComponent: ReactElement;
  /**
   * @default true
   */
  showsAssociations?: boolean;
  associationsComponent: ReactElement;
}

const ActivityItem = ({
  activity,
  showsActions = true,
  actionsComponent,
  showsAssociations = true,
  associationsComponent,
}: ActivityItemProps) => {
  const ActivityInfo = useActivityInfo();
  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const activityTitle = getActivityTitle(
    ActivityInfo[`${activity.type}`]?.title,
    activity
  );
  const ActivityIcon = ActivityInfo[`${activity.type}`]?.icon || ActivitySVG;

  const { data: userData, isSuccess: userDataIsSuccess } = useQuery(
    ['ActivityItem_userData', activity.creator],
    async () =>
      await restFactory.get<User>(`/dj/api/v1/strapi_user/${activity.creator}/`)
  );

  if (!userDataIsSuccess || !userData) {
    return null;
  }

  const handleTruncate = (_truncated: boolean) => {
    if (_truncated && _truncated !== truncated) {
      setTruncated(_truncated);
    }
  };

  const expandDescription = () => {
    truncated && setExpanded(!expanded);
  };

  const renderItemContent = () => {
    if (!ActivityInfo[activity.type]) {
      return (
        <div className={classnames(styles.activityDescription)}>
          Activity type not yet implemented
        </div>
      );
    }
    switch (activity.type) {
      case 'responded_to_request':
      case 'proposal_rejected':
      case 'proposal_accepted':
        return (
          <ActivityItemRequest
            activity={activity}
            render={(content) => formatTextNewlines(content)}
            expanded={expanded}
            setExpanded={expandDescription}
            truncated={truncated}
            handleTruncate={handleTruncate}
          />
        );
      case 'email': {
        const canTruncateContent =
          activity.description.split('<br>').length >
          MAX_ACTIVITY_DESCRIPTION_LINES;

        return (
          <ActivityItemDescription
            content={activity.description}
            render={(content) => (
              <HtmlRenderer
                className={styles.emailActivityContent}
                source={content}
              />
            )}
            canTruncateContent={canTruncateContent}
            expanded={expanded}
            setExpanded={expandDescription}
            truncated={truncated}
            handleTruncate={handleTruncate}
          />
        );
      }
      case EActivityType.link_created: {
        return (
          <div className="mt-4">
            <ActivityItemLinkCreated associations={activity.relationships} />
          </div>
        );
      }
      default:
        return (
          <ActivityItemDescription
            content={activity.description}
            render={(content) => formatTextNewlines(content)}
            expanded={expanded}
            setExpanded={expandDescription}
            truncated={truncated}
            handleTruncate={handleTruncate}
          />
        );
    }
  };

  return (
    <div
      className={classnames(styles.itemContainer)}
      data-cy={`activity:item-${activity.type}`}
    >
      <div className={classnames(styles.itemHeader)}>
        <div className={classnames(styles.itemHeader__leftContent)}>
          <ActivityIcon data-testid="activityIcon" />
          <div className={classnames(styles.activityTitleContainer)}>
            <p
              className={classnames(styles.activityHeaderText)}
              onClick={expandDescription}
            >
              {activityTitle}
            </p>
            {activity.type !== 'responded_to_request' && (
              <span className={classnames(styles.activityCreator)}>
                by {userData.first_name} {userData.last_name}
              </span>
            )}
          </div>
        </div>

        <div className={classnames(styles.itemHeader__rightContent)}>
          {showsActions && actionsComponent}
          <p className={classnames(styles.activityDate)}>
            {formatDate(activity.timestamp, false)}
          </p>
        </div>
      </div>
      {activity.type === 'email' && activity.title && (
        <p
          className={classnames(
            styles.activityHeaderText,
            styles.fromToContacts
          )}
        >{`From: ${userData.email}; To: ${getActivityContactsEmail(
          activity
        ).join(', ')}`}</p>
      )}
      {showsAssociations && (
        <div className={classnames(styles.associations)}>
          {associationsComponent}
        </div>
      )}
      {renderItemContent()}
    </div>
  );
};

export default ActivityItem;
